import { useCallback } from "react";
// テストのためにわざと react-redux から useSelector を import している
// eslint-disable-next-line no-restricted-imports
import { useSelector } from "react-redux";

import type { MyEventTicketData, MyProductData } from "@spwn/types";
import type { IssuedTicket } from "@spwn/types/firebase/firestore";
import type { Store } from "store";

export const useRelatedIssuedTicketAndGoods = () => {
  const issuedTicketStates = useSelector(
    (state: Store) => state.ticket.issuedTicketStates
  );

  // 指定 ticketId の発行済みチケットを ticketNum 昇順で返す（isGoods は除外）
  const getRelatedIssuedTickets = useCallback(
    (eventId: string, ticketId: string): IssuedTicket[] => {
      if (
        // eslint-disable-next-line no-prototype-builtins
        issuedTicketStates.hasOwnProperty(eventId) &&
        issuedTicketStates[eventId]
      ) {
        return Object.entries(issuedTicketStates[eventId])
          .filter(([_ticketNum, ticketState]) => {
            return ticketState.ticketId === ticketId && !ticketState.isGoods;
          })
          .sort(
            ([a_ticketNum, _a_ticketState], [b_ticketNum, _b_ticketState]) => {
              const a = a_ticketNum.toString().toLowerCase();
              const b = b_ticketNum.toString().toLowerCase();
              if (a < b) {
                return -1;
              } else if (a > b) {
                return 1;
              }
              return 0;
            }
          )
          .map(([ticketNum, ticketState]) => {
            ticketState["ticketNum"] = ticketNum;
            return ticketState as unknown as IssuedTicket;
          });
      }
      return [];
    },
    [issuedTicketStates]
  );

  const getRelatedIssuedTicket = useCallback(
    (
      eventId: string,
      ticketId: string,
      ticketIdx: number
    ): IssuedTicket | null => {
      if (ticketIdx === null || ticketIdx === undefined) return null;
      return getRelatedIssuedTickets(eventId, ticketId)[ticketIdx] ?? null;
    },
    [getRelatedIssuedTickets]
  );

  const getRelatedIssuedGoods = useCallback(
    (eventId: string, orderId: number): IssuedTicket | null => {
      const issuedTicket = issuedTicketStates[eventId];
      if (!issuedTicket) {
        return null;
      }
      const relatedTicket = Object.entries(issuedTicket).filter(
        ([_ticketNum, ticketState]) => {
          return ticketState.orderId === undefined
            ? ticketState.isGoods
            : ticketState.orderId === orderId && ticketState.isGoods;
        }
      );
      if (relatedTicket.length === 0) {
        return null;
      }
      // グッズ引き換えはグッズの個数に関わらず一つ
      // @ts-expect-error TS2461
      const [[ticketNum, ticketState]] = relatedTicket;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      ticketState["ticketNum"] = ticketNum;
      return ticketState as unknown as IssuedTicket;
    },
    [issuedTicketStates]
  );

  return {
    getRelatedIssuedTicket,
    getRelatedIssuedTickets,
    getRelatedIssuedGoods,
  };
};

// 自分でアプグレして「消費した」元チケットか（=非表示にすべきか）を判定する。
// 受領した共有元(owner≠自分)・未消費の元は表示。
export const isBaseTicketFunc = ({
  userId,
  eventId,
  product,
  ticketIdxCounter,
  reverseUpgradeTicketMap,
  myEventTickets,
  getRelatedIssuedTickets,
  eventTicketMap,
}: {
  userId: string;
  eventId: string | null;
  product: MyProductData;
  ticketIdxCounter: Record<string, number>;
  reverseUpgradeTicketMap: Record<string, string[]>;
  myEventTickets: MyEventTicketData["tickets"];
  getRelatedIssuedTickets: (
    eventId: string,
    ticketId: string
  ) => IssuedTicket[];
  eventTicketMap: Record<string, { allowSelfTnumGeneration?: boolean }> | null;
}) => {
  if (!eventId) return false;

  const upgradeTargetTicketId = reverseUpgradeTicketMap[product.productId]?.[0];
  if (!upgradeTargetTicketId) {
    return false;
  }
  const upgradeTargetTicket = myEventTickets.find(
    (t) => t.productId === upgradeTargetTicketId
  );
  if (!upgradeTargetTicket) {
    return false;
  }

  // QR解禁状態（ライブのマスタ flag）。購入スナップショット(product.allowSelfTnumGeneration)は
  // stale になり得るため使わない。QR解禁後だけ発行済みチケットの owner で自前/共有を精査する
  // （未解禁時は共有が発生し得ないので、従来どおり保有ベースで消費元を非表示にする）。
  const baseReleased =
    !!eventTicketMap?.[product.productId]?.allowSelfTnumGeneration;
  const upgradeReleased =
    !!eventTicketMap?.[upgradeTargetTicket.productId]?.allowSelfTnumGeneration;
  if (baseReleased && upgradeReleased) {
    const baseTickets = getRelatedIssuedTickets(eventId, product.productId);
    const ticketIndex = ticketIdxCounter[product.productId];
    if (ticketIndex === undefined) {
      return false;
    }
    const relatedTicket = baseTickets[ticketIndex];
    // 受領した共有元（owner≠自分）や未発行は消費していないので表示
    if (relatedTicket?.owner !== userId) {
      return false;
    }
    // 自分が購入したアプグレ（owner=自分）が無ければ消費していないので表示。
    // 部分アプグレは発生せず「自分の元数 == 自分のアプグレ数」が成り立つため、保有有無で判定できる。
    const ownsOwnUpgrade = getRelatedIssuedTickets(
      eventId,
      upgradeTargetTicket.productId
    ).some((t) => t.owner === userId);
    if (!ownsOwnUpgrade) {
      return false;
    }
  }

  // ここに到達 = 未解禁(保有ベース) もしくは 解禁済みで自分が消費した元。
  // クラス（7,8は配信チケット）の帰属が一致する場合のみ非表示。
  return (
    [7, 8].includes(product.class as never) ===
    [7, 8].includes(upgradeTargetTicket.class as never)
  );
};
